import React, { createContext, useState, useContext, useEffect, Children, useRef, useMemo } from 'react';
import axios from 'axios';
import { json } from 'react-router-dom';


const CART_EXPIRATION_TIME = 24 * 60 * 60 * 1000;

// Screens this wide or narrower count as mobile (isMobile).
const MOBILE_QUERY = '(max-width: 768px)';
// matchMedia is missing in some environments (e.g. jsdom in tests): treat those as desktop.
const mobileQueryList = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null);

export const ShopContext = createContext();

/*const productTest = [
    {id:1,
        imageProduct: '/images/face-cream.jpg', 
        name: 'Hyper Real Fresh Canvas Cream-To-Foam Cleanser' , 
        description: "DSAAAAAA adsaaaaaaaaaaaaa asdddddddddddddddddddddddddddddddddddddddddddd asddddddddddddddddddddddd",
        rating: 3.6,
        price: 20
    },
    {id:2, 
        imageProduct: '/images/face-cream.jpg', 
        name: 'Hyper Real Fresh Canvas Cream-To-Foam Cleanser',
        rating: 4.5,
        price: 34
    },
    {id:3, 
        imageProduct: '/images/face-cream.jpg',
        name: 'Hyper Real Fresh Canvas Cream-To-Foam Cleanser',
        rating: 2.6,
        price: 12
    },
    {id:4, 
        imageProduct: '/images/face-cream.jpg', 
        name: 'Hyper Real Fresh Canvas Cream-To-Foam Cleanser',
        rating: 5,
        price: 123.21
    }
];*/

export const ShopProvider = ({ children }) => {
    const [products, setProducts] = useState([]);
    const [isFilterVisible, setFilterVisible] = useState(true);
    const [cart, setCart] = useState([]);
    const [isCartVisible, setCartVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoriesList, setCategoriesList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('General');
    // Scents are stored in the product_type table; each variant of a product is one scent.
    const [productScents, setProductScents] = useState([]);
    // Product types (Soap, Gift Set...): a product can have several; filtered in the sidebar.
    const [productTypes, setProductTypes] = useState([]);
    const [productSizes, setProductSizes] = useState([]);
    const [userlocation, setUserLocation] = useState("CA");
    // Starts from the real screen width, so phones don't render the desktop layout first.
    const [isMobile, setIsMobile] = useState(() => mobileQueryList()?.matches ?? false);


    // Follows the width as the window is resized or the phone is rotated.
    useEffect(() => {
        const mediaQuery = mobileQueryList();
        if (!mediaQuery) return undefined;

        const handleMediaQueryChange = (event) => {
            setIsMobile(event.matches);
        };

        // Safari before 14 only has the older addListener / removeListener.
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleMediaQueryChange);
        } else {
            mediaQuery.addListener(handleMediaQueryChange);
        }

        // In case the width changed between the first render and now.
        setIsMobile(mediaQuery.matches);

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleMediaQueryChange);
            } else {
                mediaQuery.removeListener(handleMediaQueryChange);
            }
        };
    }, []);

    //Filters Selected
    const [currentFilters, setCurrentFilters] = useState({
        types: [], productTypes: [],
        sizes: [],
        prices: { min: null, max: null }
    })

    const [sortCriteria, setSortCriteria] = useState('default');
    // Bumped by resetShopFilters; the shop page uses it as the sidebar's key, so the sidebar's own
    // state (ticked boxes, price slider) starts fresh too.
    const [filterResetKey, setFilterResetKey] = useState(0);


    const cartRef = useRef(null);

    // Two product requests can overlap (e.g. clicking Shop resets the category while the page
    // reloads the list). Only the most recent one may update the list, whichever finishes last.
    const latestProductRequest = useRef(0);

    const fetchAllProducts = async () => {
        const requestId = ++latestProductRequest.current;
        try {
            const response = await axios.post('/api/ShopItems');
            if (requestId === latestProductRequest.current) setProducts(response.data);
        } catch (err) {
            console.error("Error fetching all products:", err);
        }
    };

    // Fetching Filter Stuff
    const fetchAllProductTypes = async () => {
        try {
            const response = await axios.post('/api/ProductType');
            setProductTypes(response.data);
        } catch {
            console.error("Error fetching product types")
        }
    }

    const fetchAllScents = async () => {
        try {
            const response = await axios.post('/api/ProductScent');
            setProductScents(response.data);
        } catch {
            console.error("Error fetching scents")
        }
    }

    const fetchAllSizes = async () => {
        try {
            const response = await axios.post('/api/ProductSize');
            setProductSizes(response.data);
        } catch {
            console.error("Error fetching all sizes")
        }
    }

    const fetchProductsByCategory = async (category) => {
        const requestId = ++latestProductRequest.current;
        try {
            const response = await axios.post('/api/ShopItemsByCategory', { category })
            if (requestId === latestProductRequest.current) setProducts(response.data)
        } catch (err) {
            console.error("Error fetching products by category", err);
        }
    };

    const handleCategoryChange = (category) => {
        if (category) {
            setSelectedCategory(category);
            fetchProductsByCategory(category.toLowerCase());
        } else {
            setSelectedCategory(null);
            fetchAllProducts();
        }
    };

    // Back to the unfiltered shop: all categories, no search, filters or sort.
    const resetShopFilters = () => {
        setSearchTerm('');
        setCurrentFilters({ types: [], productTypes: [], sizes: [], prices: { min: null, max: null } });
        setSortCriteria('default');
        setFilterResetKey((key) => key + 1);
        // Only reload the product list if a category had narrowed it.
        if (selectedCategory && selectedCategory !== 'General') {
            handleCategoryChange(null);
        }
    };

    // Fetch Categories
    const fetchCategoriesList = async () => {
        try {
            const response = await axios.get('/api/ShopCategories');
            setCategoriesList(response.data);
        } catch (err) {
            console.error("Error fetching categories");
        }
    }


    // Called by the shop page each time it opens (not by every page of the site). Products are
    // fetched fresh every time, so edits made in Edit Items show up without a page refresh; the
    // filter lists rarely change, so they load once per visit.
    const filterListsRequested = useRef(false);
    const loadShopData = () => {
        if (selectedCategory && selectedCategory !== 'General') {
            fetchProductsByCategory(selectedCategory.toLowerCase());
        } else {
            fetchAllProducts();
        }

        if (filterListsRequested.current) return;
        filterListsRequested.current = true;
        fetchCategoriesList();
        fetchAllScents();
        fetchAllProductTypes();
        fetchAllSizes();
        getUserLocation();
    };

    // Restore the cart saved in this browser.
    useEffect(() => {
        const savedCart = localStorage.getItem('cart');
        const savedCartTime = localStorage.getItem('cartTimestamp');

        if (savedCart && savedCartTime) {
            const currentTime = Date.now();

            if (currentTime - parseInt(savedCartTime, 10) < CART_EXPIRATION_TIME) {
                const parsedCart = JSON.parse(savedCart);
                setCart(parsedCart)
            } else {
                localStorage.removeItem('cart');
                localStorage.removeItem('cartTimestamp')
            }
        }
    }, []);

    // Save cart to localStorage when it changes
    useEffect(() => {
        if (cart.length === 0) {
            localStorage.removeItem('cart');
            localStorage.removeItem('cartTimestamp');
            return;
        }

        localStorage.setItem('cart', JSON.stringify(cart));
        localStorage.setItem('cartTimestamp', Date.now().toString());
    }, [cart]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                cartRef.current &&
                !cartRef.current.contains(e.target)
            ) {
                setCartVisible(false); // Close the cart
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [cartRef]);

    const totalCart = useMemo(() => {
        return cart.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
        );
    }, [cart]);


    const totalUniqueItems = cart.length;

    const calculateTotalItems = () => {
        return cart.reduce((total, item) => total + item.quantity, 0);
    }
    
    const calculateItemTotal = (quantity, price) => {
        return (quantity * Number(price)).toFixed(2);
    }


    // Cart lines are per variant: two scents (or sizes) of one product are separate lines.
    // `product` is a variant row (shop_item_id, variant_id, price, size...).
    const addToCart = (product, quantity = 1) => {
        setCart(prevCart => {
            const existingProduct = prevCart.find(item => item.variant_id === product.variant_id);
            if (existingProduct) {
                return prevCart.map(item =>
                    item.variant_id === product.variant_id ? { ...item, quantity: item.quantity + quantity } : item
                );
            }
            return [...prevCart, { ...product, quantity }];
        });
    };

    const removeItemCart = (variant_id) => {
        setCart(prev => prev.map(item =>
            item.variant_id === variant_id
                ? { ...item, quantity: item.quantity - 1 }
                : item).filter(item => item.quantity > 0)
        );
    };

    const removeAllSpecificItem = (variant_id) => {
        setCart(prev => prev.filter(item => item.variant_id !== variant_id));
    }

    const clearCart = () => {
        setCart([]);
    };


    const toggleVisible = () => {
        setCartVisible((prev) => !prev);
    };

    const toggleFilterVisible = () => {
        setFilterVisible((prev) => !prev)
        setCurrentFilters({
            types: [], productTypes: [],
            sizes: [],
            prices: { min: null, max: null }
        }
        )

    };



    /*const sortProducts = (results) => {
        const sortClone = [...results];

        switch (sortCriteria) {
            case 'asc':
                sortClone.sort((a, b) => a.product_name.localeCompare(b.product_name));
                break;
            case 'desc':
                sortClone.sort((a, b) => b.product_name.localeCompare(a.product_name));
                break;
            case 'rating':
                sortClone.sort((a, b) => a.rating - b.rating);
                break;
            case 'price':
                sortClone.sort((a, b) => Number(a.price) - Number(b.price))
                break;

            default:
                break;
        }


        return sortClone;
    }*/



    //Restricting by Type

    const filteredProducts = useMemo(() => {
        let results = products;

        if (searchTerm) {
            results = results.filter(prev =>
                prev.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                prev.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Each product lists every size (sizeIds) it comes in; it matches if any of its variants
        // does.
        const variantValues = (product, listKey, singleKey) =>
            Array.isArray(product[listKey]) ? product[listKey] : [product[singleKey]];

        // A product matches if it has any of the ticked product types.
        if (currentFilters.productTypes.length > 0) {
            results = results.filter(prev =>
                (prev.productTypeIds ?? []).some(id => currentFilters.productTypes.includes(id))
            );
        }

        // Scents are notes on the product (Woody, Floral...); it matches if it has any ticked one.
        if (currentFilters.types.length > 0) {
            results = results.filter(prev =>
                (prev.scentIds ?? []).some(id => currentFilters.types.includes(id))
            );
        }

        if (currentFilters.sizes.length > 0) {
            results = results.filter(prev =>
                variantValues(prev, 'sizeIds', 'size_id').some(id => currentFilters.sizes.includes(id))
            );
        }

        const { min, max } = currentFilters.prices;

        // A product is in range if any of its variants' prices is.
        if (min !== null || max !== null) {
            results = results.filter(prev => {
                const low = prev.minPrice ?? Number(prev.price);
                const high = prev.maxPrice ?? Number(prev.price);
                return (min == null || high >= min) && (max == null || low <= max);
            });
        }

        const sorted = [...results];

        switch (sortCriteria) {
            // "Newest": most recently added first; products added at the same time go A–Z.
            case 'default': sorted.sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at) || a.product_name.localeCompare(b.product_name)); break;
            case 'asc':  sorted.sort((a, b) => a.product_name.localeCompare(b.product_name)); break;
            case 'desc': sorted.sort((a, b) => b.product_name.localeCompare(a.product_name)); break;
            case 'rating': sorted.sort((a, b) => b.rating - a.rating); break;
            case 'price_low_to_high': sorted.sort((a, b) => Number(b.price) - Number(a.price)); break;
            case 'price_high_to_low': sorted.sort((a, b) => Number(a.price) - Number(b.price)); break;
            default: break;
        }
        return sorted;
    }, [products, searchTerm, currentFilters, sortCriteria]);

    

    

    // The visitor's country; loaded with the rest of the shop data (see loadShopData).
    async function getUserLocation() {
        try {
            const response = await axios.get('/api/location');
            setUserLocation(response.data.country);
        } catch (error) {
            console.error('Error fetching user location:', error);
        }
    }



    return (
        <ShopContext.Provider value={{
            products: filteredProducts,
            cart,
            addToCart,
            removeItemCart,
            clearCart,
            isCartVisible,
            setCartVisible,
            cartRef,
            toggleVisible,
            totalCart,
            calculateItemTotal,
            setSearchTerm,
            searchTerm,
            categoriesList,
            handleCategoryChange,
            selectedCategory,
            removeAllSpecificItem,
            totalUniqueItems,
            calculateTotalItems,
            isFilterVisible,
            toggleFilterVisible,
            productScents,
            productTypes,
            productSizes,
            setCurrentFilters,
            setSortCriteria,
            sortCriteria,
            resetShopFilters,
            loadShopData,
            filterResetKey,
            userlocation,
            isMobile
        }}>
            {children}
        </ShopContext.Provider>
    );

};

